import {ChangeDetectionStrategy, Component, computed, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {finalize} from "rxjs/operators";
import {AlertService} from "../../services/alert.service";
import {StateService} from "../../services/state.service";
import {UtilService} from "../../services/util.service";
import {AuthService} from "../../services/auth.service";
import {Constants} from "../../util/constants";
import {fieldMatch, passwordQuality} from "../../util/app-validators";
import {Passwords} from "../../util/passwords";
import {BusyService} from "../../services/busy.service";
import {SessionService} from "../../services/session.service";
import {MatButton} from '@angular/material/button';
import {PasswordInputComponent} from '../../controls/password-input/password-input.component';
import {MatInput} from '@angular/material/input';
import {MatError, MatFormField} from '@angular/material/form-field';
import {form, FormField, pattern, required} from "@angular/forms/signals";

const MSG_PASSWORD_UPDATED = "The password was updated correctly";

interface ResetPasswordModel {
    email: string;
    password: string;
    password2: string;
}

@Component({
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatInput, MatError, PasswordInputComponent, MatButton, FormField]
})
export class ResetPasswordComponent implements OnInit {

    isBusy = computed(() => this.busyService.isBusy());
    resetPasswordModel = signal<ResetPasswordModel>({
        email: '',
        password: '',
        password2: ''
    });
    resetPasswordForm = form(this.resetPasswordModel, (schemaPath) => {
        required(schemaPath.email, {message: Constants.ERR_REQUIRED});
        pattern(schemaPath.email, Constants.EMAIL_REGEX, {message: Constants.ERR_INVALID_EMAIL});
        required(schemaPath.password, {message: Constants.ERR_REQUIRED});
        passwordQuality(schemaPath.password, Passwords.DEFAULT_OPTIONS, {messages: {
                minLength: Constants.ERR_PASSWORD_LENGTH,
                letters: Constants.ERR_PASSWORD_LETTERS_DIGITS,
                digits: Constants.ERR_PASSWORD_LETTERS_DIGITS,
                allowSpaces: Constants.ERR_PASSWORD_SPACES
            }});
        required(schemaPath.password2, {message: Constants.ERR_REQUIRED});
        fieldMatch(schemaPath.password2, schemaPath.password, {message: Constants.ERR_PASSWORD_MISMATCH});
    });

    readonly passwordHint: string = Passwords.GetHint();
    private resetToken?: string;

    constructor(private route: ActivatedRoute,
                private alertService: AlertService,
                private state: StateService,
                private sessionService: SessionService,
                private utilService: UtilService,
                private busyService: BusyService,
                private authService: AuthService) {
    }

    ngOnInit() {
        this.resetToken = this.route.snapshot.params['token'];
        if (this.authService.isLoggedIn()){
            this.busyService.showBusy();
            this.authService.logOut().pipe(
                finalize(() => {this.busyService.showNotBusy()})
            ).subscribe({
                next: () => {
                    this.state.clearAll();
                    this.sessionService.clearSession();
                },
                error: this.utilService.getErrorHandler("Error in auto logout")
            });
        }
    }

    submit(event: SubmitEvent) {
        event.preventDefault();
        const email = this.resetPasswordForm.email().value();
        const resetToken = this.resetToken || '';
        const password = this.resetPasswordForm.password().value();
        this.busyService.showBusy();
        this.authService.resetPassword(email, resetToken, password).pipe(
            finalize(() => {
                this.busyService.showNotBusy();
            })
        ).subscribe({
                next: () => {
                    this.alertService.infoObs(MSG_PASSWORD_UPDATED)
                        .subscribe(() => {
                            this.state.clearAll();
                            this.authService.redirectToLogIn();
                        });
                },
                error: this.utilService.getErrorHandler("Error resetting password")
            }
        );
    }
}
